<template>
  <v-expansion-panel :id="`${testId}`">
    <v-expansion-panel-header>
      <h2 style="grid-area: header" :id="testName + '-title'">
        {{ testName }}
      </h2>
    </v-expansion-panel-header>
    <v-expansion-panel-content>
      <div>
        <v-form
          ref="form"
          v-model="valid"
          lazy-validation
          class="grid-container"
          id="whole-mito-test-grid"
        >
          <div>
            <p>
              Amplicon based, high coverage next generation sequencing (NGS)
              provides sensitive analysis of
              <a href="https://www.vcgs.org.au/tests/mito-DNA/" target="_blank">
                mtDNA</a
              >. This assay detects any heteroplasmic or homoplasmic single
              nucleotide variant (SNV) as well as large deletions in a single
              assay.
            </p>
            <p>
              Note: heteroplasmy levels of large deletions are an estimate only,
              they cannot be accurately determined due to inherent PCR bias.
              This assay is not validated for the detection of large
              duplications.
            </p>
            <p>
              For testing of nuclear-encoded mitochondrial genes, please see the
              <a
                href="https://www.vcgs.org.au/tests/exome-sequencing/"
                target="_blank"
              >
                Exome Sequencing</a
              >
              or
              <a
                href="https://www.vcgs.org.au/tests/whole-genome-sequencing/"
                target="_blank"
              >
                Whole Genome Sequencing</a
              >
              test options.
            </p>
          </div>
          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Turn around time*</span>
              <v-select
                v-model="isRapid"
                :items="tatOptions"
                item-text="key"
                item-value="value"
                :id="testId + '.urgency'"
                class="mb-5"
                outlined
                :rules="ruleValidBoolean"
                hide-details="auto"
              ></v-select>
            </v-col>
          </v-row>

          <v-row>
            <v-col class="custom-col">
              <span class="text-title">Clinical notes</span>
              <v-textarea
                style="grid-area: clinicalNotes"
                v-model="clinicalNotes"
                :id="testId + '.clinicalNotes'"
                outlined
              ></v-textarea>
              <span class="text-hint"
                >e.g. clinical indication for testing, relevant family history,
                results of relevant prior genetic and non-genetic
                investigations, biochemistry, histopathology and imaging,
                etc.</span
              >
            </v-col>
          </v-row>
          <TestPriceAlert :price="price" :medicare="medicare" />
        </v-form>
      </div>
      <v-btn
        class="primary-btn btn-padding-medium top-spaced-item"
        :id="testId + '.next_section'"
        @click="goToNextSection()"
      >
        Continue to next section
      </v-btn>
    </v-expansion-panel-content>
  </v-expansion-panel>
</template>

<script>
import FormRulesMixin from "../../mixins/FormRulesMixin";
import { useSectionStore } from "../../store/SectionStore";
import TestPriceAlert from "../common/TestPriceAlert.vue";
import RetrieveJSONMixin from "../../mixins/RetrieveJSONMixin.vue";
import { EventBus } from "@/event-bus";

export default {
  props: {
    testName: String,
    testId: String,
    patientdata: Object,
  },
  components: { TestPriceAlert },
  mixins: [FormRulesMixin, RetrieveJSONMixin],
  name: "",
  data: () => ({
    simpleTestPricing: null,
    tatOptions: [
      { key: "Standard", value: false },
      { key: "Rapid", value: true },
    ],
    isRapid: null,
    urgentKit: false,
    urgentReason: "",
    clinicalNotes: "",
  }),
  async mounted() {
    EventBus.$emit("handleTestsLoadingStatus", true, this.testId);
    this.simpleTestPricing = await this.execRetrieveJson(
      "json/pricing/whole_mito_pricing.json"
    );
    EventBus.$emit("handleTestsLoadingStatus", false, this.testId);
  },
  watch: {
    price: function() {
      this.$emit("toggleTotalPrice", this.price, this.testId);
    },
  },
  computed: {
    getPriceDetails() {
      if (!this.simpleTestPricing) return null;
      let rapidness = this.isRapid ? "rapid" : "standard";
      return this.simpleTestPricing[rapidness];
    },
    price() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.price;
      } else {
        return null;
      }
    },
    testCode() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.testCode;
      } else {
        return "";
      }
    },
    medicare() {
      let priceDetails = this.getPriceDetails;
      if (priceDetails) {
        return priceDetails.medicare;
      } else {
        return null;
      }
    },
  },
  methods: {
    transferData: function() {
      return {
        testType: this.testId,
        urgency: this.isRapid, // Determined by Turn around time value
        urgent: this.isRapid, // When `urgency` is true, `urgent` is true by default
        urgentReason: this.urgentReason,
        clinicalNotes: this.clinicalNotes,
        quotedPrice: this.dollarPrice(this.price),
        testCode: this.testCode,
        medicare: this.medicare,
      };
    },
    goToNextSection() {
      //wait for the panel to open before scrolling to the top
      const state = useSectionStore();
      const currIndex = state.testOrderDom.indexOf(`${this.testId}`);
      if (currIndex + 1 < state.AddedOrderofPanels.length) {
        setTimeout(() => {
          this.$vuetify.goTo(`#${state.testOrderDom[currIndex + 1]}`);
        }, 500);
        state.nextPanel(state.testOrderDom[currIndex + 1]);
      } else {
        //If there is no section after the currently opened section
        state.panel = [];
      }
    },
  },
};
</script>
